import {
  OffboardingReason,
  RequestStatus,
  RequestItemStatus,
  OffboardingAction,
  TaskPhase
} from '../web-api-client';

// Offboarding Reason display mapping
const offboardingReasonLabels: Record<OffboardingReason, string> = {
  [OffboardingReason.Resigned]: 'Resigned',
  [OffboardingReason.Retrenched]: 'Retrenched',
  [OffboardingReason.Dismissed]: 'Dismissed',
  [OffboardingReason.Retired]: 'Retired',
  [OffboardingReason.ExpiryOfContract]: 'Contract Expiry',
  [OffboardingReason.Deceased]: 'Deceased',
  [OffboardingReason.Other]: 'Other',
};

export const getOffboardingReasonText = (reason: OffboardingReason | undefined | null): string => {
  if (reason === undefined || reason === null) return '-';
  return offboardingReasonLabels[reason] ?? '-';
};

// For dropdowns/selects - returns array of options
export const offboardingReasonOptions = Object.entries(offboardingReasonLabels).map(
  ([value, label]) => ({ value: Number(value) as OffboardingReason, label })
);

// Request Status display mapping
const requestStatusLabels: Record<RequestStatus, string> = {
  [RequestStatus.Draft]: 'Draft',
  [RequestStatus.Pending]: 'Pending',
  [RequestStatus.PendingFirstApproval]: 'Pending 1st Approval',
  [RequestStatus.PendingSecondApproval]: 'Pending 2nd Approval',
  [RequestStatus.Approved]: 'Approved',
  [RequestStatus.InProgress]: 'In Progress',
  [RequestStatus.Completed]: 'Completed',
  [RequestStatus.Rejected]: 'Rejected',
  [RequestStatus.Cancelled]: 'Cancelled',
};

export const getRequestStatusText = (status: RequestStatus | undefined | null): string => {
  if (status === undefined || status === null) return 'Unknown';
  return requestStatusLabels[status] ?? 'Unknown';
};

// Status badge colors
export const getStatusBadgeColor = (status: RequestStatus | undefined): string => {
  switch (status) {
    case RequestStatus.Draft:
      return 'bg-gray-100 text-gray-800';
    case RequestStatus.Pending:
    case RequestStatus.PendingFirstApproval:
    case RequestStatus.PendingSecondApproval:
      return 'bg-yellow-100 text-yellow-800';
    case RequestStatus.Approved:
    case RequestStatus.InProgress:
      return 'bg-blue-100 text-blue-800';
    case RequestStatus.Completed:
      return 'bg-green-100 text-green-800';
    case RequestStatus.Rejected:
    case RequestStatus.Cancelled:
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// ============================================
// Request Item Status
// ============================================

const requestItemStatusLabels: Record<RequestItemStatus, string> = {
  [RequestItemStatus.Pending]: 'Pending',
  [RequestItemStatus.Completed]: 'Completed',
  [RequestItemStatus.Rejected]: 'Rejected',
  [RequestItemStatus.Returned]: 'Returned',
};

export const getRequestItemStatusText = (status: RequestItemStatus | undefined | null): string => {
  if (status === undefined || status === null) return '-';
  return requestItemStatusLabels[status] ?? '-';
};

// Badge styles for request item status (with border)
export const getRequestItemStatusBadge = (status: RequestItemStatus | undefined | null): {
  bgColor: string;
  textColor: string;
  borderColor: string;
  label: string;
} => {
  switch (status) {
    case RequestItemStatus.Pending:
      return {
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-400',
        label: 'Pending'
      };
    case RequestItemStatus.Completed:
      return {
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-500',
        label: 'Completed'
      };
    case RequestItemStatus.Rejected:
      return {
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-400',
        label: 'Rejected'
      };
    case RequestItemStatus.Returned:
      return {
        bgColor: 'bg-teal-100',
        textColor: 'text-teal-800',
        borderColor: 'border-teal-400',
        label: 'Returned'
      };
    default:
      return {
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-300',
        label: 'Unknown'
      };
  }
};

// ============================================
// Offboarding Action
// ============================================

const offboardingActionLabels: Record<OffboardingAction, string> = {
  [OffboardingAction.Outstanding]: 'Outstanding',
  [OffboardingAction.Revoked]: 'Revoked',
  [OffboardingAction.Returned]: 'Returned',
  [OffboardingAction.Extended]: 'Extended',
};

export const getOffboardingActionText = (action: OffboardingAction | undefined | null): string => {
  if (action === undefined || action === null) return '-';
  return offboardingActionLabels[action] ?? '-';
};

// Badge styles for offboarding action
export const getOffboardingActionBadge = (action: OffboardingAction | undefined | null): {
  bgColor: string;
  textColor: string;
  borderColor: string;
  label: string;
} => {
  switch (action) {
    case OffboardingAction.Outstanding:
      return {
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-400',
        label: 'Outstanding'
      };
    case OffboardingAction.Revoked:
      return {
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-500',
        label: 'Revoked'
      };
    case OffboardingAction.Returned:
      return {
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-500',
        label: 'Returned'
      };
    case OffboardingAction.Extended:
      return {
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-500',
        label: 'Extended'
      };
    default:
      return {
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-300',
        label: '-'
      };
  }
};

// ============================================
// Task Phase
// ============================================

const taskPhaseLabels: Record<TaskPhase, string> = {
  [TaskPhase.Onboarding]: 'Onboarding',
  [TaskPhase.PostOnboarding]: 'Post-Onboarding',
};

export const getTaskPhaseText = (phase: TaskPhase | undefined | null): string => {
  if (phase === undefined || phase === null) return '-';
  return taskPhaseLabels[phase] ?? '-';
};

export const getTaskPhaseBadgeColor = (phase: TaskPhase | undefined): string => {
  switch (phase) {
    case TaskPhase.Onboarding:
      return 'bg-purple-100 text-purple-800';
    case TaskPhase.PostOnboarding:
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
